import type { AgeTransitionEvent } from '../shared/types';
import { AGE_CHAPTERS, AGE_TRANSITION_TIMING as T } from './age-chapters';

const esc=(value:string)=>value.replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]!);
interface TransitionOptions {
  covered:()=>void;
  finished?:()=>void;
  schedule?:(callback:()=>void,ms:number)=>ReturnType<typeof setTimeout>;
  unschedule?:(timer:ReturnType<typeof setTimeout>)=>void;
}

/** A viewport-sized cinematic layer, independent of table markup and rules. */
export class AgeTransition {
  private dialog:HTMLDialogElement|null=null;
  private timers:ReturnType<typeof setTimeout>[]=[];
  private previousFocus:HTMLElement|null=null;
  private wasInert=false;
  private covered=false;
  private readonly doc:Document;
  private readonly reduced:MediaQueryList;
  private readonly schedule:NonNullable<TransitionOptions['schedule']>;
  private readonly unschedule:NonNullable<TransitionOptions['unschedule']>;
  constructor(private readonly table:HTMLElement,private readonly options:TransitionOptions){
    this.doc=table.ownerDocument;
    this.reduced=this.doc.defaultView!.matchMedia('(prefers-reduced-motion: reduce)');
    this.schedule=options.schedule ?? ((cb,ms)=>setTimeout(cb,ms));
    this.unschedule=options.unschedule ?? (timer=>clearTimeout(timer));
  }
  get active():boolean{return !!this.dialog;}
  get holdingOldTable():boolean{return !!this.dialog && !this.covered;}
  play(event:AgeTransitionEvent):void {
    const chapter=AGE_CHAPTERS[event.toStageIndex];if(!chapter)return;
    this.cancel();
    this.previousFocus=this.doc.activeElement as HTMLElement|null;
    this.wasInert=this.table.inert;this.table.inert=true;this.covered=false;
    const dialog=this.doc.createElement('dialog');this.dialog=dialog;
    dialog.className='age-transition';dialog.dataset.eventId=event.id;dialog.dataset.ageStage=chapter.stage;
    dialog.setAttribute('aria-labelledby','age-transition-title');dialog.setAttribute('aria-describedby','age-transition-copy');dialog.setAttribute('aria-modal','true');dialog.tabIndex=-1;
    for(const [key,value] of Object.entries({paper:chapter.paper,light:chapter.light,ink:chapter.ink,accent:chapter.accent}))dialog.style.setProperty(`--chapter-${key}`,value);
    dialog.style.setProperty('--age-from',String(Math.max(0,event.fromStageIndex)/5));
    dialog.style.setProperty('--age-to',String(event.toStageIndex/5));
    dialog.innerHTML=`<div class="age-transition-paper" aria-hidden="true"></div><div class="age-transition-content"><p class="age-transition-chapter">${esc(chapter.titleZh)}<span lang="en">${esc(chapter.titleEn)}</span></p><h1 id="age-transition-title" class="age-transition-age">${esc(chapter.ageZh)}<span lang="en">${esc(chapter.ageEn)}</span></h1><p id="age-transition-copy" class="age-transition-copy">${esc(chapter.copyZh)}<span lang="en">${esc(chapter.copyEn)}</span></p><ol class="age-transition-timeline" aria-label="人生时间轴 / Life timeline">${AGE_CHAPTERS.map((c,i)=>`<li class="${i===event.toStageIndex?'current':i<event.toStageIndex?'past':''}" ${i===event.toStageIndex?'aria-current="step"':''}><i aria-hidden="true"></i><span>${c.stage.replace('-','–')}</span></li>`).join('')}</ol></div><button type="button" class="age-transition-skip">跳过<span lang="en">Skip</span></button>`;
    dialog.querySelector('button')!.addEventListener('click',()=>this.skip());
    dialog.addEventListener('cancel',event=>{event.preventDefault();this.skip();});
    dialog.addEventListener('keydown',event=>{if(event.key==='Tab'){event.preventDefault();dialog.querySelector<HTMLButtonElement>('button')!.focus();}});
    this.doc.body.appendChild(dialog);
    this.doc.body.classList.add('age-transition-playing');
    if(typeof dialog.showModal==='function')dialog.showModal();else dialog.setAttribute('open','');
    dialog.focus({preventScroll:true});
    if(this.reduced.matches){
      dialog.dataset.reduced='true';this.markCovered();
      this.timers.push(this.schedule(()=>this.skip(),T.reducedEnd));
    }else{
      this.timers.push(this.schedule(()=>this.markCovered(),T.covered));
      this.timers.push(this.schedule(()=>{if(this.dialog){this.dialog.dataset.leaving='true';this.doc.body.classList.add('age-transition-leaving');}},T.leaving));
      this.timers.push(this.schedule(()=>this.skip(),T.end));
    }
    this.reduced.addEventListener('change',this.onMotionChange);
  }
  private readonly onMotionChange=():void=>{if(this.reduced.matches)this.skip();};
  private markCovered():void {if(this.dialog && !this.covered){this.covered=true;this.options.covered();}}
  skip():void {if(!this.dialog)return;this.markCovered();this.close();this.options.finished?.();}
  /** Disconnect/leave: dispose local visuals without inventing a game action. */
  cancel():void {if(this.dialog)this.close();}
  private close():void {
    for(const timer of this.timers)this.unschedule(timer);this.timers=[];
    this.reduced.removeEventListener('change',this.onMotionChange);
    this.dialog?.remove();this.dialog=null;this.covered=false;
    this.table.inert=this.wasInert;
    this.doc.body.classList.remove('age-transition-playing','age-transition-leaving');
    const focus=this.previousFocus?.isConnected?this.previousFocus:this.table.querySelector<HTMLElement>('.table-seat.current,button:not(:disabled)');
    if(!this.table.inert)focus?.focus({preventScroll:true});this.previousFocus=null;
  }
}
